import styles from './SiteFooter.scss';

import { ReferencedEntry } from 'contentful';
import i18n from 'i18next';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Route, Switch } from 'react-router-dom';

import { ContentType, Navigation, NavigationType } from 'src/api/contentful/contentTypes.cloverdotcom';
import { fetchEntries } from 'src/api/contentful/fetchEntries';
import { componentKey, get, getField } from 'src/utils/EntryUtil';

import { getBaseUrlFromLocale } from 'src/utils/AppRoute';
import { Environment } from 'src/utils/Environment';
import * as Locales from 'src/utils/Locales';
import { SimpleArrowAccordion } from '../Accordion/SimpleArrowAccordion/SimpleArrowAccordion';
import { AccordionItem } from '../Accordion/models';
import { withBrowser } from '../Responsive/WithBrowser';
import { useSalesChannel } from '../SalesChannel/SalesChannel';
import { TermsAndConditions } from '../TermsAndConditions/TermsAndConditions';
import { VerticalMenuTrigger } from '../VerticalMenuTrigger/VerticalMenuTrigger';
import { LocalePicker } from './LocalePicker/LocalePicker';
import { buildNewPath } from './LocalePicker/Region';
import { SiteFooterSection } from './SiteFooterSection/SiteFooterSection';

type FooterLink = {
  name: string;
  href: string;
  dataTest?: string;
  isInternal: boolean;
};

type FooterSectionTitle = string;

type FooterSections = Record<FooterSectionTitle, FooterLink[]>;

interface SiteFooterProps extends WithTranslation {
  entry?: ReferencedEntry<Navigation>;
  mobile?: boolean;
}

export type FooterLinkProps = {
  entry?: ReferencedEntry<Navigation>;
  mobile?: boolean;
};

const GENERIC_LINKS: FooterLink[] = [
  {
    name: 'terms',
    href: 'terms',
    isInternal: true,
  },
  {
    name: 'privacy-policy',
    href: 'privacy-policy',
    isInternal: true,
  },
  {
    name: 'accessibility',
    href: 'accessibility',
    isInternal: true,
    dataTest: 'footer-accessibility-link',
  },
];

const makeAccordionSections = (entry?: ReferencedEntry<Navigation>): AccordionItem[] =>
  getField(entry, 'items', []).map((item, index) => ({
    id: componentKey(item, index),
    title: getField(item, 'title', ''),
    body: (
      <SiteFooterSection
        entry={item}
        className={styles.accordionBody}
        hideTitle
      />
    ),
  }));

const FooterLinks: React.FC<FooterLinkProps> = ({ entry, mobile }) => {
  return (
    <>
      { mobile ? (
          <SimpleArrowAccordion items={makeAccordionSections(entry)} />
        ) : (
          <nav className={styles.sectionContainer}>
            {getField(entry, 'items', []).map((item, index) => <SiteFooterSection key={componentKey(item, index)} entry={item} />)}
          </nav>
        )
      }
      <hr className={styles.genericContainerBorder} />
    </>
  );
};

const SiteFooter: React.FC<SiteFooterProps> = ({ mobile, entry, t }) => {
  const { salesChannel } = useSalesChannel();
  const currentLocale = Locales.getLocale(i18n.language);
  const baseUrl = getBaseUrlFromLocale(currentLocale.code) as string;

  const shouldDisplayLinks = !Environment.isTdReseller(getField(salesChannel, 'referralId'));

  return (
    <div className={styles.wrapper}>
      {shouldDisplayLinks && <FooterLinks entry={entry} mobile={mobile} />}
      <TermsAndConditions className={styles.disclosureBlurbWrapper} />
      <nav className={styles.genericContainer}>
        <ul role="list" className={styles.genericSection}>
          <li>
            {`\u00A9 ${new Date().getFullYear()} Clover Network, LLC`}
          </li>
          {GENERIC_LINKS.map(link => (
            <li key={link.name} className={styles.link}>
              <a href={buildNewPath(baseUrl, link.href)} {...Boolean(link.dataTest) && { 'data-test': link.dataTest }}>{t(link.name as any)}</a>
            </li>
          ))}
          {i18n.language === 'en-US' && (
            <li className={styles.link}>
              <button onClick={event => { event.preventDefault(); window.evidon.notice.showPreferencesDialog(); }}>
                {t('manage-cookies')}
              </button>
            </li>
          )}
        </ul>
        <div className={styles.countrySelector}>
          <LocalePicker />
        </div>
      </nav>
    </div>
  );
};

const TranslatedFooter = withTranslation()(SiteFooter);

const Browsered = withBrowser(browser => ({ mobile: browser.is.mobile }))(TranslatedFooter);
type Browsered = typeof Browsered;

interface ContentfulSiteFooterPropsUiState {
  entry?: ReferencedEntry<Navigation>;
}

class ContentfulSiteFooter extends React.Component<WithTranslation, ContentfulSiteFooterPropsUiState> {

  readonly state: ContentfulSiteFooterPropsUiState = {};

  componentDidMount() {
    const { i18n } = this.props;

    this.fetchEntries(i18n.language);
  }

  fetchEntries(language: string) {
    fetchEntries<Navigation>(language, {
      contentType: ContentType.NAVIGATION,
      include: 3,
      filters: { fields: { type: NavigationType.FOOTER } },
    }).then(entries => this.setState({
      entry: get(entries, 'items', [])[0] as ReferencedEntry<Navigation>,
    }));
  }

  componentDidUpdate(prevProps: WithTranslation) {
    // Cannot compare this.props.i18n.language: https://github.com/i18next/react-i18next/issues/831
    if (this.props.t !== prevProps.t) {
      this.fetchEntries(this.props.i18n.language); // If the i18n.language has changed
    }
  }

  render() {
    const { entry } = this.state;

    return (
      <Switch>
        <Route
          exact
          path="/"
          render={() => (
            <VerticalMenuTrigger
              threshold={.3}
              ignoreTriggerTill={2}
              children={({ ref }) => (
                <footer data-test="footer" className={styles.footer} ref={ref}>
                  <Browsered entry={entry} />
                </footer>
              )}
            />
          )}
        />
        <Route
          render={() => (
            <footer data-test="footer" className={styles.footer}>
              <Browsered entry={entry} />
            </footer>
          )}
        />
      </Switch>
    );
  }
}

const TranslatedContentfulSiteFooter = withTranslation()(ContentfulSiteFooter);
type TranslatedContentfulSiteFooter = typeof TranslatedContentfulSiteFooter;

export {
  FooterLink,
  FooterLinks,
  FooterSections,
  FooterSectionTitle,
  TranslatedFooter as _SiteFooter,
  TranslatedContentfulSiteFooter as SiteFooter,
};
